<template>
  <div class="authorized-signatures-section w-full">
    <!-- Single Signature Mode (e.g. for Branch / Single Entity) -->
    <div v-if="single">
      <div class="flex items-center justify-between mb-2">
        <label class="text-xs font-bold text-slate-700 uppercase tracking-wider block">
          {{ title || 'Authorized Signature' }}
        </label>
        <v-btn v-if="value && !showSingleEdit" text x-small color="primary" class="normal-case font-semibold"
          @click="showSingleEdit = true">
          <v-icon x-small left>mdi-pencil</v-icon> Change Signature
        </v-btn>
      </div>

      <!-- Single Signature Configured Preview -->
      <div v-if="value && !showSingleEdit"
        class="p-3 border border-slate-200 rounded-xl bg-slate-50 flex items-center justify-between shadow-sm">
        <div class="flex items-center gap-3">
          <div
            class="bg-white p-2 rounded-lg border border-slate-200 shadow-sm flex items-center justify-center min-w-[100px] h-14">
            <img :src="resolveMediaUrl(value)" class="max-h-10 object-contain" alt="Authorized Signature" />
          </div>
          <div>
            <div class="text-xs font-bold text-slate-800">Authorized Signature Active</div>
            <div class="text-[11px] text-slate-500">
              Attached to documents and letters for this branch
            </div>
          </div>
        </div>
        <div class="flex items-center gap-1">
          <v-btn small text color="primary" class="normal-case text-xs font-semibold" @click="showSingleEdit = true">
            <v-icon small left>mdi-pencil</v-icon> Edit
          </v-btn>
          <v-btn icon small color="error" title="Remove Signature" @click="removeSingleSignature">
            <v-icon small>mdi-trash-can-outline</v-icon>
          </v-btn>
        </div>
      </div>

      <!-- Single Signature Creator Card -->
      <div v-else class="p-4 border border-slate-200 rounded-xl bg-white shadow-sm">
        <div class="flex items-center justify-between mb-3 pb-2 border-b border-slate-100">
          <h4 class="text-xs font-bold uppercase tracking-wider text-slate-700">
            {{ value ? 'Change Authorized Signature' : 'Configure Authorized Signature' }}
          </h4>
          <v-btn v-if="value" icon x-small color="slate-400" @click="showSingleEdit = false">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </div>

        <!-- Mode Tabs: Upload (Default) vs Type -->
        <div class="border-b border-slate-200 mb-4 flex items-center gap-6">
          <button type="button" class="pb-2 text-xs font-bold transition-all border-b-2" :class="newSignature.mode === 'upload'
              ? 'border-primary text-primary'
              : 'border-transparent text-slate-500 hover:text-slate-800'
            " @click="newSignature.mode = 'upload'">
            <v-icon x-small left :color="newSignature.mode === 'upload' ? 'primary' : 'grey'">
              mdi-upload
            </v-icon>
            Upload Image (Recommended)
          </button>
          <button type="button" class="pb-2 text-xs font-bold transition-all border-b-2" :class="newSignature.mode === 'type'
              ? 'border-primary text-primary'
              : 'border-transparent text-slate-500 hover:text-slate-800'
            " @click="newSignature.mode = 'type'">
            <v-icon x-small left :color="newSignature.mode === 'type' ? 'primary' : 'grey'">
              mdi-format-text
            </v-icon>
            Type Signature
          </button>
        </div>

        <!-- UPLOAD MODE -->
        <div v-if="newSignature.mode === 'upload'" class="space-y-3">
          <div
            class="flex flex-col sm:flex-row items-center gap-4 p-4 border-2 border-dashed border-slate-300 rounded-xl bg-slate-50 justify-between">
            <div class="flex items-center gap-3">
              <div v-if="newSignature.uploadedPreview"
                class="relative bg-white p-2 rounded-lg border border-slate-200 shadow-sm">
                <img :src="newSignature.uploadedPreview" class="h-14 max-w-[150px] object-contain"
                  alt="Signature preview" />
                <v-btn icon x-small color="error" class="absolute -top-2 -right-2 bg-white rounded-full shadow"
                  @click="clearUploadedSignature">
                  <v-icon x-small>mdi-close</v-icon>
                </v-btn>
              </div>
              <div v-else
                class="w-14 h-14 rounded-lg border border-dashed border-slate-300 flex items-center justify-center bg-white">
                <v-icon color="slate-400">mdi-image-plus</v-icon>
              </div>
              <div>
                <div class="text-xs font-bold text-slate-700">
                  {{ newSignature.uploadedPreview ? 'Signature Image Uploaded' : 'Upload Signature Image' }}
                </div>
                <div class="text-[11px] text-slate-400">
                  PNG with transparent background recommended (Max 2MB)
                </div>
              </div>
            </div>

            <div class="flex items-center gap-2">
              <input type="file" ref="sigFileInputSingle" style="display: none" accept="image/*"
                @change="handleFileUpload" />
              <v-btn outlined small color="primary" class="text-none font-semibold" :loading="fileUploading"
                @click="$refs.sigFileInputSingle.click()">
                <v-icon small left>mdi-upload</v-icon>
                {{ newSignature.uploadedPreview ? 'Change Image' : 'Select File' }}
              </v-btn>
            </div>
          </div>
        </div>

        <!-- TYPE MODE -->
        <div v-else-if="newSignature.mode === 'type'" class="space-y-3">
          <v-text-field v-model="newSignature.typedText" outlined dense
            placeholder="Type name to generate signature fonts..." class="text-xs" prepend-inner-icon="mdi-format-text"
            hide-details="auto"></v-text-field>

          <!-- Font Styles Grid -->
          <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            <div v-for="f in signatureFonts" :key="f.id"
              class="p-3 rounded-xl border-2 cursor-pointer transition-all flex flex-col items-center justify-center min-h-[90px] relative hover:shadow-md"
              :class="newSignature.selectedFontId === f.id
                  ? 'border-primary bg-blue-50/50 shadow-sm'
                  : 'border-slate-200 bg-white hover:border-slate-300'
                " @click="newSignature.selectedFontId = f.id">
              <span v-if="previewText" class="text-xl text-slate-800 text-center select-none truncate max-w-full px-2"
                :style="{ fontFamily: f.font }">
                {{ previewText }}
              </span>
              <span v-else class="text-xs text-slate-400 italic">
                Type text above to preview
              </span>
              <span class="text-[10px] text-slate-400 mt-1.5 font-medium">{{ f.name }}</span>
              <v-icon v-if="newSignature.selectedFontId === f.id" x-small color="primary"
                class="absolute top-2 right-2">
                mdi-check-circle
              </v-icon>
            </div>
          </div>
        </div>

        <!-- Action Footer -->
        <div class="mt-4 pt-3 border-t border-slate-100 flex items-center justify-end gap-2">
          <v-btn v-if="value" text small class="normal-case font-semibold" @click="showSingleEdit = false">
            Cancel
          </v-btn>
          <v-btn color="primary" class="white--text normal-case font-semibold px-4" small :loading="creating"
            @click="submitSingleSignature">
            <v-icon small left>mdi-check</v-icon>
            Apply Signature
          </v-btn>
        </div>
      </div>
    </div>

    <!-- Multi Signature Library Mode (e.g. for Organization Settings) -->
    <div v-else>
      <!-- Section Header -->
      <div class="flex items-center justify-between mb-4">
        <h4 class="text-md font-semibold text-slate-800">{{ title || 'Authorized Signatures' }}</h4>
        <v-btn v-if="signatures && signatures.length > 0 && !showAddForm" text color="primary"
          class="normal-case px-2 font-semibold" @click="showAddForm = true">
          <v-icon left>mdi-plus</v-icon> Add Signature
        </v-btn>
      </div>

      <!-- Configured Signatures Library Grid (Shown when signatures exist) -->
      <div v-if="signatures && signatures.length > 0" class="mb-6">
        <div class="flex items-center justify-between mb-3">
          <div class="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-2">
            <span>Configured Signatures ({{ signatures.length }})</span>
          </div>
          <div v-if="showAddForm" class="text-xs text-slate-400">
            <v-btn text x-small color="slate-500" class="normal-case" @click="showAddForm = false">
              <v-icon x-small left>mdi-chevron-up</v-icon> Hide Form
            </v-btn>
          </div>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-4">
          <div v-for="(sig, sIdx) in signatures" :key="sIdx"
            class="p-4 border rounded-xl bg-white shadow-sm flex flex-col justify-between relative group hover:shadow-md transition-shadow"
            :class="sig.isDefault ? 'border-blue-400 ring-1 ring-blue-200' : 'border-slate-200'">
            <div class="flex items-center justify-between mb-2">
              <span class="text-xs font-bold text-slate-800 truncate">
                {{ sig.signatureName || 'Signature' }}
              </span>
              <v-chip v-if="sig.isDefault" x-small color="blue lighten-5" text-color="blue darken-3"
                class="text-[9px] font-bold">
                DEFAULT
              </v-chip>
            </div>

            <div
              class="py-2 px-3 flex items-center justify-center bg-slate-50 rounded-lg border border-slate-100 min-h-[75px] mb-2">
              <img :src="resolveMediaUrl(sig.signatureUrl)" class="max-h-12 object-contain" alt="Signature" />
            </div>

            <div class="flex items-center justify-between text-[11px] text-slate-500 pt-1 border-t border-slate-100">
              <span class="truncate mr-2 font-medium">{{ sig.signatoryName || '-' }}</span>
              <div class="flex items-center gap-1">
                <v-btn v-if="!sig.isDefault" x-small text color="primary"
                  class="text-none text-[10px] px-1 font-semibold" @click="setDefault(sIdx)">
                  Set Default
                </v-btn>
                <v-btn icon x-small color="error" title="Delete Signature" @click="remove(sIdx)">
                  <v-icon x-small>mdi-trash-can-outline</v-icon>
                </v-btn>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- New Signature Creator Card -->
      <div v-if="!signatures || signatures.length === 0 || showAddForm"
        class="p-5 border border-slate-200 rounded-xl bg-white mb-6 shadow-sm">
        <div class="flex items-center justify-between mb-4 pb-2 border-b border-slate-100">
          <h4 class="text-xs font-bold uppercase tracking-wider text-slate-700">
            {{ signatures && signatures.length > 0 ? 'Add Another Authorized Signature' : 'Add Authorized Signature' }}
          </h4>
          <v-btn v-if="signatures && signatures.length > 0" icon x-small color="slate-400" @click="showAddForm = false">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label class="text-xs font-bold text-slate-700 block mb-1">Signature name *</label>
            <v-text-field v-model="newSignature.signatureName" outlined dense placeholder="Signature name"
              hint="Signature name is for your internal reference" persistent-hint class="text-xs"></v-text-field>
          </div>

          <div>
            <label class="text-xs font-bold text-slate-700 block mb-1">Signatory / Authority Name</label>
            <v-text-field v-model="newSignature.signatoryName" outlined dense placeholder="Signatory / Authority Name"
              hint="Printed as the authorized signatory name on invoices and receipts" persistent-hint class="text-xs"
              @input="onSignatoryNameInput"></v-text-field>
          </div>
        </div>

        <!-- Mode Tabs: Upload (Default) vs Type -->
        <div class="border-b border-slate-200 mb-4 flex items-center gap-6">
          <button type="button" class="pb-2 text-xs font-bold transition-all border-b-2" :class="newSignature.mode === 'upload'
              ? 'border-primary text-primary'
              : 'border-transparent text-slate-500 hover:text-slate-800'
            " @click="newSignature.mode = 'upload'">
            <v-icon x-small left :color="newSignature.mode === 'upload' ? 'primary' : 'grey'">
              mdi-upload
            </v-icon>
            Upload (Recommended)
          </button>
          <button type="button" class="pb-2 text-xs font-bold transition-all border-b-2" :class="newSignature.mode === 'type'
              ? 'border-primary text-primary'
              : 'border-transparent text-slate-500 hover:text-slate-800'
            " @click="newSignature.mode = 'type'">
            <v-icon x-small left :color="newSignature.mode === 'type' ? 'primary' : 'grey'">
              mdi-format-text
            </v-icon>
            Type
          </button>
        </div>

        <!-- UPLOAD MODE (DEFAULT) -->
        <div v-if="newSignature.mode === 'upload'" class="space-y-4">
          <div
            class="flex flex-col sm:flex-row items-center gap-4 p-4 border-2 border-dashed border-slate-300 rounded-xl bg-slate-50 justify-between">
            <div class="flex items-center gap-3">
              <div v-if="newSignature.uploadedPreview"
                class="relative bg-white p-2 rounded-lg border border-slate-200 shadow-sm">
                <img :src="newSignature.uploadedPreview" class="h-16 max-w-[160px] object-contain"
                  alt="Signature preview" />
                <v-btn icon x-small color="error" class="absolute -top-2 -right-2 bg-white rounded-full shadow"
                  @click="clearUploadedSignature">
                  <v-icon x-small>mdi-close</v-icon>
                </v-btn>
              </div>
              <div v-else
                class="w-16 h-16 rounded-lg border border-dashed border-slate-300 flex items-center justify-center bg-white">
                <v-icon color="slate-400">mdi-image-plus</v-icon>
              </div>
              <div>
                <div class="text-xs font-bold text-slate-700">
                  {{ newSignature.uploadedPreview ? 'Signature Image Uploaded' : 'Upload Signature Image' }}
                </div>
                <div class="text-[11px] text-slate-400">
                  PNG with transparent background recommended (Max 2MB)
                </div>
              </div>
            </div>

            <div class="flex items-center gap-2">
              <input type="file" ref="sigFileInput" style="display: none" accept="image/*" @change="handleFileUpload" />
              <v-btn outlined small color="primary" class="text-none font-semibold" :loading="fileUploading"
                @click="$refs.sigFileInput.click()">
                <v-icon small left>mdi-upload</v-icon>
                {{ newSignature.uploadedPreview ? 'Change Image' : 'Select File' }}
              </v-btn>
            </div>
          </div>
        </div>

        <!-- TYPE MODE -->
        <div v-else-if="newSignature.mode === 'type'" class="space-y-4">
          <v-text-field v-model="newSignature.typedText" outlined dense placeholder="Type text to generate signatures"
            class="text-xs" prepend-inner-icon="mdi-format-text"></v-text-field>

          <!-- Font Styles Grid -->
          <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            <div v-for="f in signatureFonts" :key="f.id"
              class="p-4 rounded-xl border-2 cursor-pointer transition-all flex flex-col items-center justify-center min-h-[110px] relative hover:shadow-md"
              :class="newSignature.selectedFontId === f.id
                  ? 'border-primary bg-blue-50/50 shadow-sm'
                  : 'border-slate-200 bg-white hover:border-slate-300'
                " @click="newSignature.selectedFontId = f.id">
              <span v-if="previewText" class="text-2xl text-slate-800 text-center select-none truncate max-w-full px-2"
                :style="{ fontFamily: f.font }">
                {{ previewText }}
              </span>
              <span v-else class="text-xs text-slate-400 italic">
                Type text above to preview
              </span>
              <span class="text-[10px] text-slate-400 mt-2 font-medium">{{ f.name }}</span>
              <v-icon v-if="newSignature.selectedFontId === f.id" x-small color="primary"
                class="absolute top-2 right-2">
                mdi-check-circle
              </v-icon>
            </div>
          </div>
        </div>

        <!-- Action Footer -->
        <div class="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between">
          <v-checkbox v-model="newSignature.isDefault" label="Set as default signature for all invoices" dense
            hide-details color="primary" class="text-xs"></v-checkbox>

          <v-btn color="primary" class="white--text normal-case font-semibold px-4" small :loading="creating"
            @click="submitNewSignature">
            <v-icon small left>mdi-plus-circle</v-icon>
            Save Signature
          </v-btn>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AuthorizedSignaturesSection',

  props: {
    signatures: {
      type: Array,
      default: () => []
    },
    getMediaUrl: {
      type: Function,
      default: null
    },
    single: {
      type: Boolean,
      default: false
    },
    value: {
      type: String,
      default: ''
    },
    title: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      creating: false,
      fileUploading: false,
      showAddForm: false,
      showSingleEdit: false,
      newSignature: {
        signatureName: '',
        signatoryName: '',
        designation: 'Authorized Signatory',
        mode: 'upload', // Upload by default
        typedText: '',
        selectedFontId: 'dancing-script',
        uploadedFile: null,
        uploadedPreview: null,
        uploadedS3Url: '',
        isDefault: true
      },
      signatureFonts: [
        { id: 'dancing-script', name: 'Dancing Script', font: "'Dancing Script', cursive" },
        { id: 'pacifico', name: 'Pacifico', font: "'Pacifico', cursive" },
        { id: 'great-vibes', name: 'Great Vibes', font: "'Great Vibes', cursive" },
        { id: 'alex-brush', name: 'Alex Brush', font: "'Alex Brush', cursive" },
        { id: 'allura', name: 'Allura', font: "'Allura', cursive" },
        { id: 'caveat', name: 'Caveat', font: "'Caveat', cursive" },
        { id: 'sacramento', name: 'Sacramento', font: "'Sacramento', cursive" },
        { id: 'satisfy', name: 'Satisfy', font: "'Satisfy', cursive" },
        { id: 'cedarville-cursive', name: 'Cedarville Cursive', font: "'Cedarville Cursive', cursive" }
      ]
    }
  },

  computed: {
    previewText() {
      return (
        this.newSignature.typedText ||
        this.newSignature.signatoryName ||
        this.newSignature.signatureName ||
        ''
      )
    }
  },

  methods: {
    onSignatoryNameInput() {
      if (!this.newSignature.typedText) {
        this.newSignature.typedText = this.newSignature.signatoryName
      }
    },

    clearUploadedSignature() {
      this.newSignature.uploadedPreview = ''
      this.newSignature.uploadedS3Url = ''
      this.newSignature.uploadedFile = null
    },

    resolveMediaUrl(url) {
      if (!url) return ''
      if (this.getMediaUrl && typeof this.getMediaUrl === 'function') {
        return this.getMediaUrl(url)
      }
      if (typeof url === 'object') {
        url = url.url || url.location || ''
      }
      if (typeof url !== 'string') return ''
      if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) {
        return url
      }
      return `${process.env.VUE_APP_BASE_URL || ''}${url.startsWith('/') ? '' : '/'}${url}`
    },

    async handleFileUpload(e) {
      const file = e.target.files[0]
      if (!file) return
      if (file.size > 2 * 1024 * 1024) {
        this.$toast.error('File size must be under 2MB')
        return
      }

      this.fileUploading = true
      try {
        const formData = new FormData()
        formData.append('attachments', file)
        formData.append('type', 'signature')
        const response = await this.$axios.post('/org/logo', formData)
        const s3Url =
          response.data?.url?.doc_physical_path ||
          response.data?.url?.Key ||
          response.data?.url ||
          response.data?.data?.url ||
          ''
        if (s3Url && typeof s3Url === 'string') {
          this.newSignature.uploadedS3Url = s3Url
          this.newSignature.uploadedPreview = this.resolveMediaUrl(s3Url)
          this.$toast.success('Signature image uploaded successfully')
        } else {
          throw new Error('Upload failed')
        }
      } catch (err) {
        console.error('Error uploading signature image:', err)
        this.$toast.error('Failed to upload signature image')
      } finally {
        this.fileUploading = false
      }
    },

    async generateSignatureFromText(text, fontName) {
      try {
        if (document.fonts) {
          await document.fonts.load(`56px "${fontName}"`)
        }
      } catch (e) {
        console.warn('Font loading check error', e)
      }

      const canvas = document.createElement('canvas')
      canvas.width = 600
      canvas.height = 200
      const ctx = canvas.getContext('2d')
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      ctx.fillStyle = '#0f172a'
      ctx.font = `56px "${fontName}", cursive`
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText(text, canvas.width / 2, canvas.height / 2)

      return new Promise((resolve, reject) => {
        canvas.toBlob(async (blob) => {
          if (!blob) {
            reject(new Error('Canvas blob generation failed'))
            return
          }
          try {
            const file = new File([blob], `signature_${Date.now()}.png`, { type: 'image/png' })
            const formData = new FormData()
            formData.append('attachments', file)
            formData.append('type', 'signature')
            const response = await this.$axios.post('/org/logo', formData)
            const s3Url =
              response.data?.url?.doc_physical_path ||
              response.data?.url?.Key ||
              response.data?.url ||
              response.data?.data?.url ||
              ''
            resolve(typeof s3Url === 'string' ? s3Url : '')
          } catch (err) {
            reject(err)
          }
        }, 'image/png')
      })
    },

    async submitSingleSignature() {
      this.creating = true
      try {
        let signatureUrl = ''
        if (this.newSignature.mode === 'type') {
          const textToRender = this.newSignature.typedText?.trim()
          if (!textToRender) {
            this.$toast.error('Please type a signature name to generate signature')
            this.creating = false
            return
          }
          const selectedFontObj =
            this.signatureFonts.find((f) => f.id === this.newSignature.selectedFontId) ||
            this.signatureFonts[0]
          signatureUrl = await this.generateSignatureFromText(textToRender, selectedFontObj.name)
        } else {
          if (!this.newSignature.uploadedS3Url) {
            this.$toast.error('Please upload a signature image file first')
            this.creating = false
            return
          }
          signatureUrl = this.newSignature.uploadedS3Url
        }

        if (!signatureUrl) {
          throw new Error('Failed to generate or upload signature image')
        }

        this.$emit('input', signatureUrl)
        this.$emit('change', signatureUrl)
        this.showSingleEdit = false
        this.clearUploadedSignature()
        this.newSignature.typedText = ''
        this.$toast.success('Signature applied')
      } catch (err) {
        console.error('Error saving single signature:', err)
        this.$toast.error(err.response?.data?.message || err.message || 'Failed to save signature')
      } finally {
        this.creating = false
      }
    },

    removeSingleSignature() {
      this.$emit('input', '')
      this.$emit('change', '')
      this.showSingleEdit = false
      this.clearUploadedSignature()
      this.newSignature.typedText = ''
    },

    async submitNewSignature() {
      if (!this.newSignature.signatureName || !this.newSignature.signatureName.trim()) {
        this.$toast.error('Please enter a Signature Name for reference')
        return
      }

      this.creating = true
      try {
        let signatureUrl = ''
        if (this.newSignature.mode === 'type') {
          const textToRender =
            this.newSignature.typedText ||
            this.newSignature.signatoryName ||
            this.newSignature.signatureName ||
            'Signature'
          const selectedFontObj =
            this.signatureFonts.find((f) => f.id === this.newSignature.selectedFontId) ||
            this.signatureFonts[0]
          signatureUrl = await this.generateSignatureFromText(textToRender, selectedFontObj.name)
        } else {
          if (!this.newSignature.uploadedS3Url) {
            this.$toast.error('Please upload a signature image file first')
            this.creating = false
            return
          }
          signatureUrl = this.newSignature.uploadedS3Url
        }

        if (!signatureUrl) {
          throw new Error('Failed to generate or upload signature image')
        }

        const currentList = Array.isArray(this.signatures) ? [...this.signatures] : []
        const isFirst = currentList.length === 0
        const isDefault = this.newSignature.isDefault || isFirst

        if (isDefault) {
          currentList.forEach((s) => {
            s.isDefault = false
          })
        }

        const newEntry = {
          signatureName: this.newSignature.signatureName.trim(),
          signatoryName: (
            this.newSignature.signatoryName ||
            this.newSignature.typedText ||
            this.newSignature.signatureName
          ).trim(),
          designation: (this.newSignature.designation || 'Authorized Signatory').trim(),
          signatureUrl: signatureUrl,
          isDefault: isDefault
        }

        currentList.push(newEntry)

        this.$emit('signatures-updated', currentList)
        this.$emit('save')

        // Reset form for adding another signature
        this.newSignature = {
          signatureName: '',
          signatoryName: '',
          designation: 'Authorized Signatory',
          mode: 'upload', // default back to upload
          typedText: '',
          selectedFontId: 'dancing-script',
          uploadedFile: null,
          uploadedPreview: null,
          uploadedS3Url: '',
          isDefault: true
        }
        this.showAddForm = false
        this.$toast.success('Signature saved successfully')
      } catch (err) {
        console.error('Error creating signature:', err)
        this.$toast.error(err.response?.data?.message || err.message || 'Failed to create signature')
      } finally {
        this.creating = false
      }
    },

    setDefault(index) {
      const currentList = Array.isArray(this.signatures) ? [...this.signatures] : []
      currentList.forEach((s, idx) => {
        s.isDefault = idx === index
      })
      this.$emit('signatures-updated', currentList)
      this.$emit('save')
      this.$toast.success('Default signature updated')
    },

    remove(index) {
      const currentList = Array.isArray(this.signatures) ? [...this.signatures] : []
      const wasDefault = currentList[index]?.isDefault
      currentList.splice(index, 1)
      if (wasDefault && currentList.length > 0) {
        currentList[0].isDefault = true
      }
      this.$emit('signatures-updated', currentList)
      this.$emit('save')
      this.$toast.info('Signature removed')
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Alex+Brush&family=Allura&family=Caveat:wght@600&family=Dancing+Script:wght@600&family=Great+Vibes&family=Pacifico&family=Sacramento&family=Satisfy&family=Cedarville+Cursive&display=swap');
</style>
